/* eslint-disable array-callback-return */
function merge(a = [], b = [], prop = 'postingId') {
  const reduced = a.filter(aitem => !b.find(bitem => aitem[prop] === bitem[prop]));
  return reduced.concat(b);
}

const planWeight = {
  EXCLUSIVE: 6,
  EXCLUSIVE_2: 5,
  SUPERHIGHLIGHTED: 4,
  HIGHLIGHTED: 3,
  SIMPLE: 2,
  'FREE-SUBSIMPLE': 1,
  FREE: 1
};

const sortCredits = credits => {
  if (credits && credits.branchCredits && credits.branchCredits[0] && credits.branchCredits[0].creditItems) {
    credits.branchCredits[0].creditItems.sort((a, b) => {
      if (planWeight[a.publicationType] < planWeight[b.publicationType]) {
        return 1;
      }
      if (planWeight[a.publicationType] > planWeight[b.publicationType]) {
        return -1;
      }
      return 0;
    });
  }

  return credits || {};
};

const filterCurrentPlan = (credits, currentPlanType) => {
  // SI SE ENVÍA PLAN ACTUAL SE FILTRAN EL PLAN ACTUAL E INFERIORES EN WEIGHT
  if (currentPlanType && credits && credits[0]) {
    return credits.reduce((filteredCredits, current) => {
      if (planWeight[current.publicationType] > planWeight[currentPlanType]) {
        filteredCredits.push(current);
      }
      return filteredCredits;
    }, []);
  }
  return [];
};

const getQueryParams = () => {
  // recuperamos el querystring
  const querystringMD = window.location.search;

  // usando el querystring, creamos un objeto del tipo URLSearchParams
  return new URLSearchParams(querystringMD);
};

const getCredits = (postingsArray, postingBranch, totalCredits) => {
  // SI SE TRATA DE UN ADMINISTRADOR (TIENE CRÉDITOS DE VARIAS BRANCHES), HAY QUE IDENTIFICAR SI ES OPERACIÓN BULK O SINGLE PARA PASARLE LOS CRÉDITOS CORRESPONDIENTES
  if (totalCredits.branchCredits && totalCredits.branchCredits.length > 1) {
    if (postingsArray.length > 1) {
      // SI ES OPERACIÓN EN BULK SE TOMAN LOS CRÉDITOS GLOBALES, NO SE PUEDEN SEPARAR POR BRANCH
      return totalCredits.globalCredits;
    }
    // SI ES UN SOLO POSTING SE FILTRA PARA QUE SE UTILICEN SOLO LOS CRÉDITOS DE LA BRANCH A LA QUE PERTENECE EL POSTING
    return totalCredits.branchCredits.some(credit => parseInt(credit.branchId) === postingBranch)
      ? totalCredits.branchCredits.filter(credit => parseInt(credit.branchId) === postingBranch)[0]
      : [];
  }
  // SI NO TIENE VARIAS BRANCHES ENTONCES NO ES NECESARIO FILTRAR
  if (totalCredits.branchCredits && totalCredits.branchCredits[0]) {
    return totalCredits.branchCredits[0];
  }
  return {};
};

const getPublisherByPosting = (postingsList, postingsToPublishList) => {
  return postingsList.reduce((publisherMap, currentPosting) => {
    if (postingsToPublishList.some(postingId => postingId === currentPosting.postingId)) {
      publisherMap[currentPosting.postingId] = currentPosting.publisherId;
    }
    return publisherMap;
  }, {});
};

/**
 * La función valida si los postings seleccionados son 'ONLINE' y cuántos créditos dispone por cada tipo de plan.
 * @param {object} postingsInfo trae todos los postings con su status y tipo de planId. Ej.: {54639002: {publicationPlanId:"1", status:"ONLINE"}}
 * @returns {object} objeto que indica por cada branchId la cantidad de avisos (ONLINE) por tipo de planId. Ej.:
 * {54639002:{1:4, 52:6 }}
 */
const getSameActivePlanPostings = (postingsAndPublishers, postingsInfo) => {
  // Se filtran los postings que estén 'ONLINE'
  const getOnlinePostings =
    postingsInfo && Object.keys(postingsInfo).filter(postingId => postingsInfo[postingId].status === 'ONLINE');
  // Se arma objeto por branchId con la cantidad de avisos (ONLINE) por tipo de planId:
  return (
    getOnlinePostings?.reduce((postingsObj, postingId) => {
      const { publicationPlanId } = postingsInfo[postingId];
      const branchId = postingsAndPublishers?.[postingId];
      // Se validan valores nulos o indefinidos:
      if (!branchId || !publicationPlanId) {
        return postingsObj;
      }
      // Se inicializa y actualiza el contador por branchId y planId.
      if (!postingsObj[branchId]) {
        postingsObj[branchId] = {};
      }
      if (!postingsObj[branchId][publicationPlanId]) {
        postingsObj[branchId][publicationPlanId] = 1;
      } else {
        postingsObj[branchId][publicationPlanId] += 1;
      }
      return postingsObj;
    }, {}) || null
  );
};

/**
 * Se valida si el usuario tiene suficientes créditos por branch según la cantidad de postings seleccionados.
 * @param {object} credits - Los créditos disponibles
 * @param {object} postingsAndPublishers - Relaciona postingId con branchId
 * @param {object} postingsInfo - Información de los postings, incluyendo el status y el planId
 * @returns {object} indicando primero si hay créditos insuficientes y la cantidad de avisos con el mismo plan por branch. Y dentro de postingsAvailable se indican los créditos necesarios y cant de avisos activos.
 * Ej.: {insufficientCredits:[] postingsAvailable:{necessaryCredits:5, totalSameActivePlan:{}}
 */
const getInsufficientCreditsByBranch = (credits, postingsAndPublishers, postingsInfo) => {
  if (!credits || !postingsAndPublishers || !postingsInfo) return { insufficientCredits: [], postingsAvailable: {} };

  // Obtenemos para cada sucursal (branchId) los postings activos con el mismo tipo de plan:
  const sameActivePlanPostings = getSameActivePlanPostings(postingsAndPublishers, postingsInfo);

  // Se arma objeto por sucursal (branchId) y se obtiene cuántos planes consumiría (según la cantidad de postings seleccionados) y con cuantos planes de cada tipo cuenta:
  const necessaryCreditsByPublisher = Object.keys(postingsAndPublishers).reduce((necessaryCreditsObj, postingId) => {
    const branchId = postingsAndPublishers[postingId];
    // Se validan valores nulos o indefinidos:
    if (!branchId) return necessaryCreditsObj;
    // Se inicializa el objeto por branchId:
    if (!necessaryCreditsObj[branchId]) {
      necessaryCreditsObj[branchId] = {
        availableBranchCredits: getCredits([postingId], branchId, credits),
        necessaryCredits: 1,
        totalSameActivePlan: sameActivePlanPostings?.[branchId] || 0
      };
    } else {
      // Se acumulan los créditos necesarios según la cantidad de avisos seleccionados:
      necessaryCreditsObj[branchId].necessaryCredits += 1;
    }
    return necessaryCreditsObj;
  }, {});

  //  Ahora se recorre el arreglo de créditos globales, que son los que se van a mostrar en el modal de créditos:
  //  Si alguna de las sucursales no cuenta con suficientes planes de algún tipo para cubrir todos sus postings se agrega al objeto para deshabilitarlo.
  const result = credits?.globalCredits?.creditItems?.reduce(
    (finalObj, currentCredit) => {
      Object.keys(necessaryCreditsByPublisher).forEach(branchId => {
        const { availableBranchCredits, necessaryCredits, totalSameActivePlan } = necessaryCreditsByPublisher[branchId];
        // Se recorren los créditos de cada sucursal:
        availableBranchCredits.creditItems.forEach(branchCreditItem => {
          const { publicationPlanId, availableItems } = branchCreditItem || {};
          const totalSameActivePlanByBranch = totalSameActivePlan?.[publicationPlanId] || 0;

          // Se comparan los planId's (de los créditos globales vs los de la sucursal) y se verifica si los créditos son suficientes:
          if (
            publicationPlanId === currentCredit.publicationPlanId &&
            availableItems < necessaryCredits - totalSameActivePlanByBranch
          ) {
            finalObj.insufficientCredits.push({
              branchId,
              planId: publicationPlanId
            });
          }
        });
      });

      return finalObj;
    },
    { insufficientCredits: [] }
  );

  // Adicionalemnte se añade el atributo postingsAvailable al objeto con la cantidad de creditos disponibles y el total de avisos activos:
  result.postingsAvailable = Object.keys(necessaryCreditsByPublisher).reduce((acc, branchId) => {
    const { necessaryCredits, totalSameActivePlan } = necessaryCreditsByPublisher[branchId];
    acc[branchId] = { necessaryCredits, totalSameActivePlan };
    return acc;
  }, {});

  return result;
};

/**
 * La función determina si todos los postings seleccionados pertenecen a una misma sucursal (branchId)
 * @param {object} postingsAndPublishers - Relaciona postingId con branchId
 * @returns {number | null}
 */
const getSameBranchId = postingsAndBranches => {
  const branchIds = Object.values(postingsAndBranches);
  // Se verifica si todos los branchIds son iguales al primero:
  const allEqual = branchIds.every(branchId => branchId === branchIds[0]);
  return allEqual ? branchIds[0] : null;
};

const encodeFilterValues = searchQuery => {
  const encodedArr = searchQuery.split(';').reduce((finalArr, currentValue) => {
    // SE SPLITEA POR ":" Y SE EXCLUYE EL PRIMER ÍNDICE QUE SIEMPRE ES EL NOMBRE DEL CAMPO,
    // EL RESTO SE VUELVE A JOINEAR YA QUE EL CARACTER ":" PUEDE SER PARTE DE LA BÚSQUEDA
    const value = currentValue
      .split(':')
      .slice(1)
      .join(':');
    // SE CODIFICA PARA PODER ENVIAR CARACTERES ESPECIALES EN EL PEDIDO
    finalArr.push(currentValue.replace(value, encodeURIComponent(value)));
    return finalArr;
  }, []);
  return encodedArr.join(';');
};

// Planes Freemium:
const freePlans = [6002, 6001, 6000];
// Planes Exclusivos:
const exclusivePlanIds = [52, 53, 1052, 1053];
// Plan Súper Destacado Exclusivo: 52
// Plan Destacado Exclusivo: 53
// Plan Súper Destacado Exclusivo COMBO: 1052
// Plan Destacado Exclusivo COMBO 1053:

const filterCredits = (
  credits,
  isDevelopmentPosting,
  operationType,
  planFreeSubSimple = false,
  isExclusiveZone,
  arrayWithExclusiveZoneId,
  creditsExclusive,
  mixExclusivePostings,
  isTargetFull = false
) => {
  const includesIdCreditsExclusive =
    // Se valida si alguno de los location_Id se encuentra dentro de alguno de los objetos con las zonas exclusivas y es una operación tipo Venta:
    creditsExclusive
      ? creditsExclusive.filter(
          f => f.operation_type_id === 1 && arrayWithExclusiveZoneId.some(element => f.location_id.includes(element))
        )
      : [];

  // Filtramos los créditos exclusivos y mapeamos los publication_plan_id
  const exclusivePlans =
    includesIdCreditsExclusive.length === 1 &&
    includesIdCreditsExclusive[0].publication_plans.map(c => parseInt(c.publication_plan_id));

  // En el caso de que tengamos una selección de BULK con arrayWithExclusiveZoneId de diferentes zonas de créditos, se muestran sólo los tipo 'SIMPLE', 'FREE-SIMPLE' o los 'Freemium':
  const mixExclusiveZoneId = includesIdCreditsExclusive.length > 1;

  return credits
    ? credits.filter(c => {
        // Se filtra por DESARROLLO ó CLASIFICADO:
        if (c.isDevelopment === isDevelopmentPosting && c.availableItems > 0) {
          // Se evalúan los casos en el Bulk dónde estén mezclados los avisos exclusivos y no exclusivos para devolver sólo los tipo 'SIMPLE' o los 'Freemium para Venta'. También se validan casos dónde se tengan locatio_Id de diferentes zonas exclusivas:
          if (mixExclusivePostings || mixExclusiveZoneId) {
            if (c.publicationType === 'SIMPLE' || (c.publicationPlanId === 6002 && operationType === 1)) {
              return true;
            }
            return false;
          }
          // Se evalúa que tenga un plan tipo 'SIMPLE', 'HIGHLIGHTED' ó 'SUPERHIGHLIGHTED', que no tenga 'FREE-SUBSIMPLE' ni 'FREEMIUMS' y que no se encuentre en una Zona Exlusiva:
          if (
            c.publicationType !== 'FREE-SUBSIMPLE' &&
            !freePlans.includes(c.publicationPlanId) &&
            (!isExclusiveZone && !exclusivePlanIds.includes(c.publicationPlanId))
          ) {
            return true;
          }
          // Se evalúa que el aviso se encuentre en una Zona Exclusiva para mostrar los planes exclusivos:
          if (isExclusiveZone && (exclusivePlans && exclusivePlans.includes(c.publicationPlanId))) {
            return true;
          }
          // Se evalúa que no tenga planes Freemium pero que si pueda admitir un plan 'FREE-SUBSIMPLE' (desde PublishModal recibiendo planFreeSubSimple = true) y no se encuentre en una zona exclusiva:
          if (
            !freePlans.includes(c.publicationPlanId) &&
            planFreeSubSimple &&
            (!isExclusiveZone && !exclusivePlanIds.includes(c.publicationPlanId))
          ) {
            return true;
          }
          // Se evalúa si tiene un plan 'FREEMIUM VENTA' (para particulares), es una operación de VENTA y no aplica a FULL:
          if (c.publicationPlanId === 6002 && operationType === '1' && !isTargetFull) {
            return true;
          }
          // Se evalúa si tiene un plan 'FREEMIUM ALQUILER' (para particulares), es una operación de ALQUILER y no aplica a FULL:
          if (c.publicationPlanId === 6001 && operationType === '2' && !isTargetFull) {
            return true;
          }
          // Se evalúa si tienen un plan 'FREEMIUM ALQUILER TEMPORAL' (para particulares), es una operación de ALQUILER TEMPORAL y no aplica a FULL:
          if (c.publicationPlanId === 6000 && operationType === '4' && !isTargetFull) {
            return true;
          }
        }
        return false;
      })
    : [];
};

export {
  merge,
  sortCredits,
  filterCurrentPlan,
  getCredits,
  getPublisherByPosting,
  getInsufficientCreditsByBranch,
  getQueryParams,
  encodeFilterValues,
  filterCredits,
  freePlans,
  getSameActivePlanPostings,
  getSameBranchId
};

/**
 * Elimina las etiquetas HTML (como <br>, <div>, <span>, etc.) de un texto
 *
 * @param {string} str - El texto que contiene etiquetas HTML que se desean eliminar.
 * @returns {string} - El texto sin las etiquetas HTML especificadas.
 */
export function cleanHtmlTags(str) {
  return str.replace(/<\/?(br|div|span|p|h[1-6]|a|img|ul|ol|li|table|tr|td|th|strong|em|i|b|u)\s*\/?>/gi, '');
}
